import { alpha, Box, Chip, Grid, Stack, useTheme } from '@mui/material'
import { useContactsItems } from 'api/aws/contacts/useContactItems'
import { useNotificationsByUserId } from 'api/aws/notifications/useNotificationsByUserId'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useEntitiesFromSearch } from 'api/search'
import { useSyncStatus } from 'api/search/useSearchData'
import { formatCamelCase } from 'components/DataGrid/operators/formatCamelCase'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import { ROLES } from 'config/roles.config'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { GROBxLoader } from 'theme/components'
import EntityAvatar from 'theme/components/Avatar/EntityAvatar'
import { Deal, UserOption } from 'types'
import { formatDateTime, isActiveThisMonth } from 'utils/formatDayjsDateTime'
import { sortByTimeCreated } from 'utils/sortOperators'
import { useActiveUser } from '../../api/slack/useActiveUser'
import CompactList from './CompactList'
import DealsPanel from './DealsPanel'
import RenderStatCards from './RenderStatCards'
import { RenderCurrencyPrimary, RenderOrgStats, RenderPrimary, RenderStatus, RenderTitle } from './ReusableUI'
import SelectActiveUser from './SelectActiveUser'
// ---------- helpers ----------

const toArray = (v?: string | string[]) => (Array.isArray(v) ? v : v ? [v] : [])
const isOwnedBy = (owner: string | string[] | undefined, userId?: string) => !!userId && toArray(owner).includes(userId)
const toNumber = (v: any): number => {
  if (v == null) return 0
  const n = typeof v === 'string' ? parseFloat(v) : Number(v)
  return Number.isFinite(n) ? n : 0
}
const dealContractTotal = (d: any) => toNumber(d?.totals?.total ?? d?.total)
const dealMonthlyTotal = (d: any) => toNumber(d?.totals?.monthlyTotal)
const productTotalValue = (p: any) => toNumber(p?.netTotal ?? p?.price)
// choose the best product $ field your data uses:
const productValue = (p: any) => toNumber(p?.billedPrice ?? p?.price ?? p?.total ?? p?.monthlyPrice)
const daysUntil = (iso?: string) => {
  const d = dayjs(iso)
  return d.isValid() ? d.diff(dayjs(), 'day') : Infinity
}
const endsSoonInDays = (iso?: string, n = 30) => {
  const d = daysUntil(iso)
  return d >= 0 && d <= n ? d : null
}
const withinNDays = (iso?: string, n = 30) =>
  !!iso && dayjs(iso).isValid() && dayjs(iso).isAfter(dayjs()) && dayjs(iso).diff(dayjs(), 'day') <= n

const ADMIN_IDS = ['U015KLM51B6', ROLES.BUSINESS_OWNER, 'UMU21GS7Q', 'UHWLS6L7P', ROLES.SYSTEM_ADMIN]
const DashboardPage = () => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { pinned } = useLayoutContext()
  const { activeUser, isActiveUserLoading } = useActiveUser()
  const slackUserId = activeUser?.user?.id
  const [userDash, setUserDash] = useState<string | null>(
    () => localStorage.getItem('dashboardUser') || slackUserId || null
  )

  const viewUserId = userDash ?? slackUserId ?? null
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const { data: contacts, isLoading: isContactsLoading } = useContactsItems()
  const { data: rawData, isLoading } = useNotificationsByUserId(viewUserId, !!viewUserId)
  const { deals = [], organizations = [], products = [], isItemsLoading, meta } = useEntitiesFromSearch() as any
  const { data: syncStatus } = useSyncStatus(isItemsLoading)

  const notifications = useMemo(() => {
    if (!rawData || isLoading) return []
    return rawData
      ? sortByTimeCreated(rawData?.notifications)
          //@ts-ignore
          ?.filter(r => !r?.isRead)
          ?.slice(0, 100)
      : []
  }, [rawData, isLoading])
  const userOptions: UserOption[] = useMemo(
    () =>
      (users ?? []).map(u => ({
        name: u?.name,
        first_name: u?.first_name,
        last_name: u?.last_name,
        firstName: u?.firstName,
        lastName: u?.lastName,
        id: u?.user_uuid,
        user_uuid: u?.user_uuid,
        label: u?.name || u?.email || u?.user_uuid,
        avatar: u?.image_512,
      })),
    [users]
  )
  const selectedUserOpt = userOptions.find(o => o.id === viewUserId) || null
  const currentUser = users?.find(u => u?.user_uuid === viewUserId)
  const myOrgs = useMemo(
    () => organizations.filter(o => isOwnedBy(o?.owner, viewUserId) && (o?.status || '').toLowerCase() !== 'ended'),
    [organizations, viewUserId]
  )
  const myDeals = useMemo(() => deals.filter(d => isOwnedBy(d?.owner, viewUserId)), [deals, viewUserId])
  const myProducts = useMemo(() => products.filter(p => myDeals.some(d => d?.deal_uuid === p?.deal_uuid)), [
    products,
    myDeals,
  ])
  const myOrgContactIds = useMemo(() => {
    const s = new Set<string>()
    for (const o of myOrgs) toArray(o?.billingContact).forEach(id => id && s.add(id))
    return s
  }, [myOrgs])

  ///////////////////// CONTACTS ////////////////////////////
  const myContacts = useMemo(() => (contacts ?? []).filter(c => myOrgContactIds.has(c?.contacts_uuid)), [
    contacts,
    myOrgContactIds,
  ])
  const contactOrgCount = useMemo(
    () => organizations?.filter(o => myContacts?.some(c => c?.contacts_uuid === o?.billingContact?.[0])?.length),
    [contacts, myContacts, organizations]
  )
  const orgCountByContactAll = useMemo(() => {
    const m = new Map<string, number>()
    for (const o of organizations ?? []) {
      toArray(o?.billingContact).forEach(id => {
        if (!id) return
        m.set(id, (m.get(id) || 0) + 1)
      })
    }
    return m
  }, [organizations])

  const orgCountByContactMine = useMemo(() => {
    const m = new Map<string, number>()
    for (const o of myOrgs ?? []) {
      toArray(o?.billingContact).forEach(id => {
        if (!id) return
        m.set(id, (m.get(id) || 0) + 1)
      })
    }
    return m
  }, [myOrgs])
  type ContactWithCounts = typeof contacts[number] & {
    orgCountTotal: number
    orgCountMine: number
  }
  const myContactsWithCounts: ContactWithCounts[] = useMemo(
    () =>
      myContacts.map(c => ({
        ...c,
        orgCountTotal: orgCountByContactAll.get(c?.contacts_uuid) || 0,
        orgCountMine: orgCountByContactMine.get(c?.contacts_uuid) || 0,
      })),
    [myContacts, orgCountByContactAll, orgCountByContactMine]
  )
  ////////////////////////////////////////////////////////////////////////////////////////

  // Active products (this month)
  const activeProducts = useMemo(() => myProducts.filter(p => isActiveThisMonth(p?.startDate, p?.endDate)), [
    myProducts,
  ])

  // Active deals are those with ANY active product
  const dealById = useMemo(() => {
    const m = new Map<string, Deal>()
    for (const d of myDeals) {
      if (d?.deal_uuid) m.set(d.deal_uuid, d)
    }
    return m
  }, [myDeals])
  const activeDealIds = useMemo(() => {
    const ids = activeProducts.map(p => p.deal_uuid).filter((id): id is string => !!id) // type guard: keep only strings
    return Array.from(new Set<string>(ids)) // ensure Set<string>
  }, [activeProducts])
  const activeDeals = useMemo(() => {
    return activeDealIds.map(id => dealById.get(id)).filter((d): d is Deal => !!d) // type guard -> Deal[]
  }, [activeDealIds, dealById])

  // Active orgs via active deals
  const activeOrgs = useMemo(() => {
    const orgPKs = new Set(activeDeals.map(d => d.PK))
    return myOrgs.filter(o => orgPKs.has(o.PK))
  }, [myOrgs, activeDeals])
  const productsByDealId = useMemo(() => {
    const map = new Map<string, any[]>()
    for (const p of products) {
      const arr = map.get(p?.deal_uuid) || []
      arr.push(p)
      map.set(p?.deal_uuid, arr)
    }
    return map
  }, [products])

  ///// Values
  const activeContractValue = useMemo(() => activeDeals.reduce((sum, d) => sum + dealContractTotal(d), 0), [
    activeDeals,
  ])
  const activeProductRevenue = useMemo(() => activeProducts.reduce((sum, p) => sum + productTotalValue(p), 0), [
    activeProducts,
  ])
  const orgRevenueMap = useMemo(() => {
    const map = new Map<string, number>()
    for (const d of activeDeals) {
      map.set(d.PK, (map.get(d.PK) || 0) + dealContractTotal(d))
    }
    return map
  }, [activeDeals])

  // Aggregate metrics per org from ACTIVE deals
  const orgAgg = useMemo(() => {
    const m = new Map<string, { contractTotal: number; monthlyTotal: number; dealCount: number }>()
    for (const d of activeDeals) {
      const key = d.PK
      const cur = m.get(key) || {
        contractTotal: 0,
        monthlyTotal: 0,
        dealCount: 0,
      }
      cur.contractTotal += dealContractTotal(d)
      cur.monthlyTotal += dealMonthlyTotal(d)
      cur.dealCount += 1
      m.set(key, cur)
    }
    return m
  }, [activeDeals])
  // Active MRR (sum of monthlyTotal for active deals)
  const activeMRR = useMemo(() => activeDeals.reduce((sum, d) => sum + dealMonthlyTotal(d), 0), [activeDeals])

  // MRR at risk: any active product ends ≤30d -> count that deal's monthlyTotal
  const mrrAtRisk30d = useMemo(() => {
    const riskyDealIds = new Set<string>()
    for (const p of activeProducts) {
      if (withinNDays(p?.endDate, 30)) riskyDealIds.add(p?.deal_uuid)
    }
    return activeDeals.filter(d => riskyDealIds.has(d?.deal_uuid)).reduce((sum, d) => sum + dealMonthlyTotal(d), 0)
  }, [activeProducts, activeDeals])

  const endingSoonByDeal = useMemo(() => {
    // deal_uuid -> { daysLeft: number, count: number }
    const m = new Map<string, { daysLeft: number; count: number }>()
    for (const p of activeProducts) {
      if (!p?.deal_uuid) continue
      const days = daysUntil(p?.endDate) // your helper
      if (days >= 0 && days <= 30) {
        const cur = m.get(p.deal_uuid) || { daysLeft: Infinity, count: 0 }
        const nextDays = Math.min(cur.daysLeft, days)
        m.set(p.deal_uuid, { daysLeft: nextDays, count: cur.count + 1 })
      }
    }
    return m
  }, [activeProducts])
  // KPI: deals ending in ≤30 days (based on any owned product endDate nearing)
  const dealsEndingSoonCount = useMemo(() => {
    const endingDealIds = new Set<string>()
    for (const p of activeProducts) {
      if (withinNDays(p?.endDate, 30)) endingDealIds.add(p?.deal_uuid)
    }
    return endingDealIds.size
  }, [activeProducts])

  // KPI: contracts sent this month (owned)
  const contractsSentCount = useMemo(
    () =>
      myDeals.filter(
        d =>
          (d?.stage || '').toLowerCase() === 'contract sent' &&
          dayjs(d?.updatedAt || d?.timestamp || d?.createdAt).isSame(dayjs(), 'month')
      ).length,
    [myDeals]
  )
  const dealsWonCount = useMemo(
    () =>
      myDeals.filter(
        d =>
          (d?.stage || '').toLowerCase() === 'deal won' &&
          dayjs(d?.updatedAt || d?.timestamp || d?.createdAt).isSame(dayjs(), 'month')
      ).length,
    [myDeals]
  )
  const endScore = (p: any) => daysUntil(p?.endDate)

  const topProducts = useMemo(() => {
    return [...activeProducts] // only active ones
      .sort((a, b) => {
        const byEnd = endScore(a) - endScore(b) // sooner first
        if (byEnd !== 0) return byEnd
        return productValue(b) - productValue(a) // tie-break by $
      })
    // .slice(0, 12)
  }, [activeProducts])
  const topDeals = useMemo(() => {
    return [...activeDeals].sort((a, b) => {
      const A = endingSoonByDeal.get(a.deal_uuid!)?.daysLeft ?? Infinity
      const B = endingSoonByDeal.get(b.deal_uuid!)?.daysLeft ?? Infinity
      if (A !== B) return A - B // sooner first
      const byContract = dealContractTotal(b) - dealContractTotal(a)
      return byContract !== 0 ? byContract : dealMonthlyTotal(b) - dealMonthlyTotal(a)
      // .slice(0, 12)
    })
  }, [activeDeals, endingSoonByDeal])
  const topOrgs = useMemo(() => {
    return [...activeOrgs].sort((a, b) => (orgRevenueMap.get(b?.PK) || 0) - (orgRevenueMap.get(a?.PK) || 0))
    // .slice(0, 12)
  }, [activeOrgs, orgRevenueMap])
  // if Slack user arrives later, use it as default once
  useEffect(() => {
    if (!userDash && slackUserId) setUserDash(slackUserId)
  }, [slackUserId]) // eslint-disable-line

  // persist selection
  useEffect(() => {
    if (userDash) localStorage.setItem('dashboardUser', userDash)
  }, [userDash])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: pinned ? 'calc(100vh - 20px)' : '100vh',
        pt: pinned ? 7 : 0,
        overflowY: 'auto',
        transition: 'padding 0.3s ease',
      }}
    >
      {isItemsLoading && (
        <GROBxLoader
          open={true}
          message="Syncing Dashboard"
          overlay
          steps={[
            { label: `Organizations${syncStatus?.orgCount ? ` (${syncStatus.orgCount.toLocaleString()})` : ''}`, done: !!syncStatus?.orgsReady },
            { label: `Deals${syncStatus?.dealCount ? ` (${syncStatus.dealCount.toLocaleString()})` : ''}`, done: !!syncStatus?.dealsReady },
            { label: `Products${syncStatus?.productCount ? ` (${syncStatus.productCount.toLocaleString()})` : ''}`, done: !!syncStatus?.productsReady },
            { label: 'Users', done: !isUsersLoading },
            { label: 'Contacts', done: !isContactsLoading },
          ]}
        />
      )}

      {/* ─── Header ──────────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: isDark
            ? `linear-gradient(135deg, ${alpha(theme.palette.text.highlight, 0.08)}, rgba(91, 141, 239, 0.05))`
            : `linear-gradient(135deg, ${alpha(theme.palette.text.highlight, 0.06)}, rgba(91, 141, 239, 0.03))`,
        }}
      >
        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
          {ADMIN_IDS?.includes(slackUserId) ? (
            <SelectActiveUser
              userOptions={userOptions}
              selectedUserOpt={selectedUserOpt}
              isUsersLoading={isUsersLoading}
              setUserDash={setUserDash}
            />
          ) : (
            <EntityAvatar
              photoUrl={currentUser?.image_512}
              size="sm"
              isLoading={isUsersLoading || isActiveUserLoading}
            />
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={`Orgs: ${myOrgs.length}`}
              variant="outlined"
              sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
            />
            <Chip
              size="small"
              label={`Deals: ${myDeals.length}`}
              variant="outlined"
              sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
            />
            <Chip
              size="small"
              label={`Products: ${products.filter(p => myDeals.some(d => d?.deal_uuid === p?.deal_uuid)).length}`}
              variant="outlined"
              sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
            />
          </Box>

          <Box sx={{ flex: 1 }} />

          {viewUserId && viewUserId === slackUserId && ADMIN_IDS?.includes(slackUserId) && (
            <Chip
              size="small"
              variant="outlined"
              label="You"
              sx={{ height: 22, fontSize: 11, fontWeight: 600, borderColor: 'text.highlight' }}
            />
          )}
          {ADMIN_IDS?.includes(slackUserId) && (
            <Chip
              size="small"
              variant="outlined"
              label="ADMIN"
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                borderColor: alpha(theme.palette.text.highlight, 0.4),
                color: 'primary.main',
              }}
            />
          )}
          {!ADMIN_IDS?.includes(slackUserId) && (
            <Chip
              size="small"
              variant="outlined"
              label="USER"
              sx={{ height: 22, fontSize: 11, fontWeight: 600, borderColor: 'text.review' }}
            />
          )}
        </Stack>
      </Box>

      {/* ─── Content ─────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* KPIs | Values */}
        <RenderStatCards
          activeContractValue={activeContractValue}
          activeMRR={activeMRR}
          mrrAtRisk30d={mrrAtRisk30d}
          dealsEndingSoonCount={dealsEndingSoonCount}
          dealsWonCount={dealsWonCount}
          contractsSentCount={contractsSentCount}
          activeProductRevenue={activeProductRevenue}
        />

        {/* Lists row */}
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
            <DealsPanel
              deals={deals}
              organizations={organizations}
              viewUserId={viewUserId}
              endingSoonByDeal={endingSoonByDeal}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
            <CompactList
              title={<RenderTitle title="Products (Active)" count={topProducts?.length} />}
              items={topProducts}
              primary={p => {
                return <RenderCurrencyPrimary organizations={organizations} item={p} />
              }}
              secondary={p => {
                const d = endsSoonInDays(p?.endDate, 30)
                const chip =
                  typeof d === 'number' ? (
                    <Chip
                      size="small"
                      label={`ends in ${d}d`}
                      color="warning"
                      variant="outlined"
                      sx={{
                        ml: 1,
                        px: 0.5,
                        height: 20,
                        fontSize: 12,
                        flexShrink: 0,
                        color: 'text.warning',
                      }}
                    />
                  ) : null
                return (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <RenderStatus
                      item={{
                        status: p?.status,
                        startDate: p?.startDate,
                        endDate: p?.endDate,
                      }}
                      tag={chip}
                    />
                  </Box>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
            <CompactList
              title={<RenderTitle title="Organizations (Active)" count={topOrgs?.length} />}
              items={topOrgs}
              primary={o => <RenderPrimary organizations={organizations} item={o} />}
              secondary={o => {
                const m = orgAgg.get(o?.PK) || {
                  contractTotal: 0,
                  monthlyTotal: 0,
                  dealCount: 0,
                }
                return <RenderOrgStats item={m} status={o?.status} />
              }}
            />
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <CompactList
              title={<RenderTitle title="Contacts (Affiliated)" count={myContactsWithCounts?.length ?? 0} />}
              items={myContactsWithCounts}
              primary={c => c?.name || c?.email || '—'}
              secondary={c => (
                <span>
                  Orgs (mine/total): <b>{c.orgCountMine}</b>/<b>{c.orgCountTotal}</b>
                </span>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <CompactList
              title={<RenderTitle title="Notifications (Unread)" count={notifications?.length ?? 0} />}
              items={notifications}
              primary={n => (
                <span>
                  <span>{n?.title || n?.type || '—'}</span> •{' '}
                  <span>{n?.creation_timestamp ? formatDateTime(n?.creation_timestamp) : null}</span> •{' '}
                  <span>
                    <i style={{ color: renderColorByString(n?.board) }}>{formatCamelCase(n?.board)}</i>
                  </span>
                </span>
              )}
              secondary={n => (
                <span>
                  Message: <b>{n.message}</b>
                </span>
              )}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default DashboardPage
